import * as plugins from './plugins.js';
import { type CSSResult } from 'lit';

export class DeesElement extends plugins.lit.LitElement {
  // STATIC — component-level constraint helper (use in `static styles = [...]`)

  private static getContainerName(): string {
    return (this as any).is || this.name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  static cssForConstraint(constraints: { maxWidth?: number; minWidth?: number }) {
    return (cssArg: CSSResult) =>
      plugins.domtools.breakpoints.cssForConstraintContainer(constraints, this.getContainerName())(cssArg);
  }

  // INSTANCE
  /**
   * Reflected as the `gobright` attribute so components can style themes
   * directly via `:host([gobright]) { ... }` without per-value bdTheme pairs.
   */
  @plugins.lit.property({ type: Boolean, reflect: true })
  public accessor goBright: boolean = false;

  // domtools
  public domtoolsPromise: Promise<plugins.domtools.DomTools>;

  @plugins.lit.property()
  public accessor domtools!: plugins.domtools.DomTools;

  public rxSubscriptions: plugins.smartrx.rxjs.Subscription[] = [];
  private connectionController?: AbortController;
  private lifecycleTail: Promise<void> = Promise.resolve();

  private elementDomReadyDeferred = plugins.domtools.plugins.smartpromise.defer();
  public elementDomReady = this.elementDomReadyDeferred.promise;

  constructor(optionsArg: plugins.domtools.IDomToolsContructorOptions = {}) {
    super();
    this.domtoolsPromise = plugins.domtools.elementBasic.setup(this, optionsArg);
    this.domtoolsPromise.then((domtoolsArg) => {
      this.domtools = domtoolsArg;
    });
  }

  /**
   * Called when the theme changes between bright and dark.
   * Override this method to handle theme changes.
   * @param goBright - true if switching to bright theme, false if switching to dark theme
   */
  protected themeChanged?(goBright: boolean): void;

  public connectedCallback(): Promise<void> {
    super.connectedCallback();
    const controller = new AbortController();
    this.connectionController = controller;
    return this.enqueueLifecycle(async () => {
      const domtools = await this.domtoolsPromise;
      if (controller.signal.aborted || !this.isConnected) return;
      const themeSubscription = domtools.themeManager.themeObservable.subscribe((goBrightArg) => {
        if (controller.signal.aborted || !this.isConnected) return;
        this.goBright = goBrightArg;
        this.themeChanged?.(goBrightArg);
      });
      this.rxSubscriptions.push(themeSubscription);
      for (const startupFunction of this.startupFunctions) {
        if (controller.signal.aborted || !this.isConnected) return;
        await startupFunction(controller.signal);
      }
      if (!controller.signal.aborted && this.isConnected) {
        this.dispatchEvent(new CustomEvent('deesElementConnected'));
      }
    });
  }

  public firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.firstUpdated(_changedProperties);
    this.elementDomReadyDeferred.resolve();
  }

  private startupFunctions: ((signal: AbortSignal) => void | Promise<unknown>)[] = [];
  public registerStartupFunction(startupFunctionArg: (signal: AbortSignal) => void | Promise<unknown>) {
    this.startupFunctions.push(startupFunctionArg);
  }

  private garbageFunctions: (() => void | Promise<unknown>)[] = [];
  public registerGarbageFunction(garbageFunctionArg: () => void | Promise<unknown>) {
    this.garbageFunctions.push(garbageFunctionArg);
  }

  public disconnectedCallback(): Promise<void> {
    // Lit must disconnect directives immediately, even while DOM setup is pending.
    super.disconnectedCallback();
    if (!this.connectionController) return this.lifecycleTail;
    this.connectionController.abort();
    this.connectionController = undefined;
    const errors: unknown[] = [];
    this.unsubscribeResources(errors);
    return this.enqueueLifecycle(async () => {
      // A running startup hook may finish after removal. Its cleanup still belongs
      // to this connection and must finish before the next connection starts.
      this.unsubscribeResources(errors);
      for (const garbageFunction of this.garbageFunctions) {
        try {
          await garbageFunction();
        } catch (error) {
          errors.push(error);
        }
      }
      if (!this.isConnected) this.dispatchEvent(new CustomEvent('deesElementDisconnected'));
      if (errors.length) throw new AggregateError(errors, 'DeesElement cleanup failed');
    });
  }

  private unsubscribeResources(errors: unknown[]): void {
    for (const subscription of this.rxSubscriptions.splice(0)) {
      try {
        subscription.unsubscribe();
      } catch (error) {
        errors.push(error);
      }
    }
  }

  private enqueueLifecycle(operation: () => Promise<void>): Promise<void> {
    const result = this.lifecycleTail.then(operation);
    // Keep the queue usable after an error, while returning the original rejected
    // promise to the caller so failures remain observable.
    this.lifecycleTail = result.then(() => undefined, () => undefined);
    return result;
  }
}
